import { TrackingHelper } from '@/helpers/tracking.helper';
import { Utils } from '@/helpers/utils';
import { AlertService } from '@/services/alert.service';
import { formatDate, NgFor, NgIf } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { PropertyEvent, PropertyEventType, RealProperty, TrackingService } from '@eemo/api-client';

import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

export class TimelineEventDialogData {
  prop?: RealProperty;
  event?: PropertyEvent;
  edition = false;
}

export interface EventTypeDisplay {
  type: PropertyEventType;
  display: string;
}

@Component({
  selector: 'app-timeline-event-dialog',
  templateUrl: './timeline-event-dialog.component.html',
  styleUrls: ['./timeline-event-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    NgFor,
    MatOptionModule,
    NgIf,
    MatInputModule,
    MatDatepickerModule,
    MatSlideToggleModule,
    MatButtonModule
  ]
})
export class TimelineEventDialogComponent implements OnInit {
  isBrowserSupportsDateInput = Utils.isBrowserSupportsDateInput();

  availableEventTypes: EventTypeDisplay[] = [];
  eventForm: UntypedFormGroup;
  dialogTitle: string;
  formSubmitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TimelineEventDialogData,
    private trackingService: TrackingService,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    private trackingHelper: TrackingHelper,
    public dialogRef: MatDialogRef<TimelineEventDialogComponent>,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.dialogTitle = this.data.edition ? "Modifier l'évènement" : 'Créer un évènement';
  }

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.eventForm.get(field);
  }

  ngOnInit() {
    this.trackingHelper.getUpdatablePropertyEventTypes().forEach((t) => {
      this.availableEventTypes.push({ type: t, display: this.trackingHelper.getPropertyEventTypeName(t) });
    });
    this.eventForm = this.fb.group({
      type: [PropertyEventType.WORK, Validators.required],
      date: [formatDate(new Date(), 'yyyy-MM-dd', this.locale), Validators.required],
      energyImpact: [false],
      details: ['', [Validators.required, Validators.maxLength(160)]]
    });

    if (this.data.event) {
      this.eventForm.patchValue(this.data.event);
    }
  }

  onSubmit() {
    if (this.eventForm.valid) {
      this.formSubmitting = true;
      // parse the date to format date send by mat datepicker (browser with support of input type="date" doesn't need it)
      const date = new Date(this.f('date').value);
      this.f('date').setValue(formatDate(date, 'yyyy-MM-dd', this.locale));

      if (this.data.edition) {
        this.trackingService.updatePropertyEvent(this.data.event.uid, this.eventForm.value).subscribe({
          next: () => {
            this.dialogRef.close(true);
            this.alertService.success('Mise à jour effectuée');
          },
          error: () => {
            this.dialogRef.close();
          }
        });
      } else {
        this.trackingService.addPropertyEvent(this.data.prop.uid, this.eventForm.value).subscribe({
          next: (createdId) => {
            this.dialogRef.close(createdId);
            this.alertService.success('Création effectuée');
          },
          error: () => {
            this.dialogRef.close();
          }
        });
      }
    }
  }
  onNoClick(): void {
    this.dialogRef.close(false);
  }
}
